import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Mail,
  Send,
  Users,
  FileText,
  Plus,
  Search,
  Edit,
  Trash2,
  Copy,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronRight,
  Building2,
  User,
  Sparkles,
  Save,
  X,
  Filter,
  MailOpen,
  Target,
  TrendingUp,
  Calendar,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import EmailTemplateEditor from "../components/EmailTemplateEditor";

const API = process.env.REACT_APP_BACKEND_URL;

// Default email templates
const DEFAULT_TEMPLATES = [
  {
    id: "prospection",
    name: "Prospection commerciale",
    subject: "Food Truck United - Vos événements méritent le meilleur",
    content: `Bonjour {{nom_contact}},

Je me permets de vous contacter au nom de Food Truck United, le premier réseau de food trucks professionnels de la région.

Nous accompagnons les entreprises et collectivités comme {{nom_entreprise}} dans l'organisation d'événements gourmands uniques : 
• Séminaires et team building
• Événements d'entreprise
• Marchés et festivals
• Événements privés

Nos food trucks proposent une cuisine de qualité, variée et adaptée à tous les goûts.

Seriez-vous disponible pour un échange de 15 minutes afin de découvrir nos services ?

Bien cordialement,
L'équipe Food Truck United`,
    category: "prospection"
  },
  {
    id: "relance",
    name: "Relance prospect",
    subject: "Suite à notre échange - Food Truck United",
    content: `Bonjour {{nom_contact}},

Je me permets de revenir vers vous suite à notre précédent échange concernant les services de Food Truck United.

Avez-vous eu l'occasion de réfléchir à notre proposition pour {{nom_entreprise}} ?

Je reste à votre disposition pour répondre à vos questions ou organiser une dégustation découverte.

Bien cordialement,
L'équipe Food Truck United`,
    category: "relance"
  },
  {
    id: "evenement",
    name: "Invitation événement",
    subject: "Invitation exclusive - Découvrez nos food trucks",
    content: `Bonjour {{nom_contact}},

Food Truck United a le plaisir de vous inviter à notre prochain événement de présentation.

C'est l'occasion idéale pour :
• Découvrir notre sélection de food trucks
• Déguster nos spécialités
• Échanger avec nos équipes
• Obtenir un devis personnalisé

Nous serions ravis de vous compter parmi nous !

Pour confirmer votre présence, répondez simplement à cet email.

À très bientôt,
L'équipe Food Truck United`,
    category: "invitation"
  },
  {
    id: "remerciement",
    name: "Remerciement post-événement",
    subject: "Merci pour votre confiance !",
    content: `Bonjour {{nom_contact}},

Toute l'équipe de Food Truck United tient à vous remercier pour votre confiance lors de votre dernier événement.

Nous espérons que nos services ont été à la hauteur de vos attentes et que vos convives ont apprécié notre cuisine.

N'hésitez pas à nous faire part de vos retours, ils sont précieux pour continuer à nous améliorer.

Au plaisir de collaborer à nouveau avec {{nom_entreprise}} !

Bien cordialement,
L'équipe Food Truck United`,
    category: "fidelisation"
  }
];

export default function MailingPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  // Main states
  const [activeTab, setActiveTab] = useState("campaigns");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  
  // CRM contacts
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactFilter, setContactFilter] = useState("all");
  const [searchContact, setSearchContact] = useState("");
  
  // New campaign dialog
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [campaignData, setCampaignData] = useState({
    name: "",
    subject: "",
    content: "",
    templateId: "",
  });
  
  // Template dialog
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateData, setTemplateData] = useState({
    name: "",
    subject: "",
    content: "",
    category: "prospection",
  });
  
  // Preview dialog
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: "", content: "" });
  
  // Visual editor dialog
  const [showVisualEditor, setShowVisualEditor] = useState(false);
  const [editingVisualTemplate, setEditingVisualTemplate] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0,
    recentCampaigns: 0
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load CRM contacts
      const contactsRes = await axios.get(`${API}/api/crm/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(contactsRes.data || []);
      
      // Load campaigns
      const campaignsRes = await axios.get(`${API}/api/mailing/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(campaignsRes.data || []);
      
      // Load custom templates
      const templatesRes = await axios.get(`${API}/api/mailing/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (templatesRes.data && templatesRes.data.length > 0) {
        setTemplates([...DEFAULT_TEMPLATES, ...templatesRes.data]);
      }
      
      // Calculate stats
      const sentCampaigns = (campaignsRes.data || []).filter(c => c.status === "sent");
      setStats({
        totalSent: sentCampaigns.reduce((acc, c) => acc + (c.recipients_count || 0), 0),
        totalOpened: sentCampaigns.reduce((acc, c) => acc + (c.opened_count || 0), 0),
        totalClicked: sentCampaigns.reduce((acc, c) => acc + (c.clicked_count || 0), 0),
        recentCampaigns: sentCampaigns.length
      });
      
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter contacts
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = !searchContact || 
      contact.nom_entreprise?.toLowerCase().includes(searchContact.toLowerCase()) ||
      contact.nom_contact?.toLowerCase().includes(searchContact.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchContact.toLowerCase());
    
    const matchesFilter = contactFilter === "all" || contact.statut === contactFilter;
    
    // Only show contacts with email
    const hasEmail = contact.email && contact.email.trim() !== "";
    
    return matchesSearch && matchesFilter && hasEmail;
  });

  // Select all visible contacts
  const selectAllContacts = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.id));
    }
  };

  // Toggle contact selection
  const toggleContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  // Apply template
  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setCampaignData(prev => ({
        ...prev,
        templateId,
        subject: template.subject,
        content: template.content,
      }));
    }
  };

  // Preview email with personalization
  const previewEmail = () => {
    const sampleContact = selectedContacts.length > 0 
      ? contacts.find(c => c.id === selectedContacts[0])
      : { nom_contact: "Jean Dupont", nom_entreprise: "Entreprise Exemple", email: "exemple@email.com" };
    
    let subject = campaignData.subject;
    let content = campaignData.content;
    
    // Replace variables
    subject = subject.replace(/\{\{nom_contact\}\}/g, sampleContact?.nom_contact || "Client");
    subject = subject.replace(/\{\{nom_entreprise\}\}/g, sampleContact?.nom_entreprise || "Entreprise");
    subject = subject.replace(/\{\{email\}\}/g, sampleContact?.email || "");
    
    content = content.replace(/\{\{nom_contact\}\}/g, sampleContact?.nom_contact || "Client");
    content = content.replace(/\{\{nom_entreprise\}\}/g, sampleContact?.nom_entreprise || "Entreprise");
    content = content.replace(/\{\{email\}\}/g, sampleContact?.email || "");
    
    setPreviewContent({ subject, content });
    setShowPreviewDialog(true);
  };

  // Send campaign
  const sendCampaign = async () => {
    if (!campaignData.name || !campaignData.subject || !campaignData.content) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    if (selectedContacts.length === 0) {
      toast.error("Veuillez sélectionner au moins un destinataire");
      return;
    }
    
    setSending(true);
    
    try {
      const response = await axios.post(`${API}/api/mailing/campaigns/send`, {
        name: campaignData.name,
        subject: campaignData.subject,
        content: campaignData.content,
        recipient_ids: selectedContacts,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Campagne envoyée à ${response.data.sent_count} destinataires !`);
      setShowCampaignDialog(false);
      setCampaignData({ name: "", subject: "", content: "", templateId: "" });
      setSelectedContacts([]);
      loadData();
      
    } catch (error) {
      console.error("Error sending campaign:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  // Save template
  const saveTemplate = async () => {
    if (!templateData.name || !templateData.subject || !templateData.content) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    try {
      if (editingTemplate && !DEFAULT_TEMPLATES.find(t => t.id === editingTemplate.id)) {
        await axios.put(`${API}/api/mailing/templates/${editingTemplate.id}`, templateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template mis à jour");
      } else {
        await axios.post(`${API}/api/mailing/templates`, templateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template créé");
      }
      
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      setTemplateData({ name: "", subject: "", content: "", category: "prospection" });
      loadData();
      
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  // Delete template
  const deleteTemplate = async (templateId) => {
    if (DEFAULT_TEMPLATES.find(t => t.id === templateId)) {
      toast.error("Impossible de supprimer un template par défaut");
      return;
    }
    
    if (!window.confirm("Supprimer ce template ?")) return;
    
    try {
      await axios.delete(`${API}/api/mailing/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Delete campaign
  const deleteCampaign = async (campaignId) => {
    if (!window.confirm("Supprimer cette campagne ?")) return;
    
    try {
      await axios.delete(`${API}/api/mailing/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campagne supprimée");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Save visual template
  const saveVisualTemplate = async (templateData) => {
    try {
      const payload = {
        name: templateData.name,
        subject: templateData.subject,
        content: "", // Plain text version fallback
        category: "visual",
        blocks: templateData.blocks,
        html: templateData.html,
        is_visual: true,
      };
      
      if (editingVisualTemplate && !DEFAULT_TEMPLATES.find(t => t.id === editingVisualTemplate.id)) {
        await axios.put(`${API}/api/mailing/templates/${editingVisualTemplate.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template visuel mis à jour");
      } else {
        await axios.post(`${API}/api/mailing/templates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template visuel créé");
      }
      
      setShowVisualEditor(false);
      setEditingVisualTemplate(null);
      loadData();
      
    } catch (error) {
      console.error("Error saving visual template:", error);
      toast.error("Erreur lors de la sauvegarde du template");
    }
  };

  // Apply visual template for campaign
  const applyVisualTemplateForCampaign = (template) => {
    setCampaignData(prev => ({
      ...prev,
      subject: template.subject,
      content: template.html || "",
      templateId: template.id,
    }));
    setShowCampaignDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 bg-[#FAF9F7] min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-secondary flex items-center gap-3">
            <Mail className="w-8 h-8 text-primary" />
            Mailing & Campagnes
          </h1>
          <p className="text-muted-foreground mt-1">
            Créez et envoyez des campagnes email personnalisées
          </p>
        </div>
        <Button 
          onClick={() => setShowCampaignDialog(true)} 
          className="bg-primary hover:bg-primary-hover gap-2"
        >
          <Plus className="w-4 h-4" />
          Nouvelle campagne
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Send className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.totalSent}</p>
                <p className="text-xs text-blue-600">Emails envoyés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <MailOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.totalOpened}</p>
                <p className="text-xs text-green-600">Ouvertures</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{stats.recentCampaigns}</p>
                <p className="text-xs text-purple-600">Campagnes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{contacts.filter(c => c.email).length}</p>
                <p className="text-xs text-orange-600">Contacts email</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="campaigns" className="gap-2">
            <Send className="w-4 h-4" />
            Campagnes
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="contacts" className="gap-2">
            <Users className="w-4 h-4" />
            Contacts
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Historique des campagnes</span>
                <Button variant="outline" size="sm" onClick={loadData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg font-medium text-muted-foreground">Aucune campagne</p>
                  <p className="text-sm text-muted-foreground mt-1">Créez votre première campagne email</p>
                  <Button className="mt-4" onClick={() => setShowCampaignDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle campagne
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {campaigns.map((campaign) => (
                    <div 
                      key={campaign.id} 
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg hover:bg-gray-50 gap-4"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold">{campaign.name}</h4>
                          <Badge 
                            variant={campaign.status === "sent" ? "default" : "secondary"}
                            className={campaign.status === "sent" ? "bg-green-100 text-green-700" : ""}
                          >
                            {campaign.status === "sent" ? "Envoyée" : "Brouillon"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{campaign.subject}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {campaign.recipients_count || 0} destinataires
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(campaign.sent_at || campaign.created_at), "dd/MM/yyyy HH:mm", { locale: fr })}
                          </span>
                          {campaign.status === "sent" && (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-3 h-3" />
                              {campaign.opened_count || 0} ouvertures
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setCampaignData({
                              name: campaign.name + " (copie)",
                              subject: campaign.subject,
                              content: campaign.content,
                              templateId: "",
                            });
                            setShowCampaignDialog(true);
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700"
                          onClick={() => deleteCampaign(campaign.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Templates d'emails</span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setEditingVisualTemplate(null);
                      setShowVisualEditor(true);
                    }}
                    className="gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    Éditeur visuel
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setEditingTemplate(null);
                      setTemplateData({ name: "", subject: "", content: "", category: "prospection" });
                      setShowTemplateDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Template texte
                  </Button>
                </div>
              </CardTitle>
              <CardDescription>
                Utilisez les variables {"{{nom_contact}}"}, {"{{nom_entreprise}}"}, {"{{email}}"} pour personnaliser vos emails
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <Card key={template.id} className={`hover:shadow-md transition-shadow ${template.is_visual ? 'border-primary/30 bg-primary/5' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold">{template.name}</h4>
                            {DEFAULT_TEMPLATES.find(t => t.id === template.id) && (
                              <Badge variant="secondary" className="text-xs">Défaut</Badge>
                            )}
                            {template.is_visual && (
                              <Badge className="text-xs bg-primary/20 text-primary border-primary/30">
                                <Sparkles className="w-3 h-3 mr-1" />
                                Visuel
                              </Badge>
                            )}
                          </div>
                          <Badge variant="outline" className="mt-1 text-xs">
                            {template.category === "prospection" ? "Prospection" :
                             template.category === "relance" ? "Relance" :
                             template.category === "invitation" ? "Invitation" :
                             template.category === "fidelisation" ? "Fidélisation" :
                             template.category === "visual" ? "Visuel" : template.category}
                          </Badge>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {template.subject}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              if (template.is_visual) {
                                setEditingVisualTemplate(template);
                                setShowVisualEditor(true);
                              } else {
                                setEditingTemplate(template);
                                setTemplateData({
                                  name: template.name,
                                  subject: template.subject,
                                  content: template.content,
                                  category: template.category,
                                });
                                setShowTemplateDialog(true);
                              }
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {!DEFAULT_TEMPLATES.find(t => t.id === template.id) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500"
                              onClick={() => deleteTemplate(template.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-3"
                        onClick={() => {
                          if (template.is_visual && template.html) {
                            applyVisualTemplateForCampaign(template);
                          } else {
                            setCampaignData(prev => ({
                              ...prev,
                              subject: template.subject,
                              content: template.content,
                              templateId: template.id,
                            }));
                            setShowCampaignDialog(true);
                          }
                        }}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Utiliser ce template
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Contacts CRM avec email</CardTitle>
              <CardDescription>
                {filteredContacts.length} contacts disponibles pour vos campagnes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher un contact..."
                    value={searchContact}
                    onChange={(e) => setSearchContact(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={contactFilter} onValueChange={setContactFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filtrer par statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="lead">Nouveaux leads</SelectItem>
                    <SelectItem value="prospect">Prospects</SelectItem>
                    <SelectItem value="contacte">Contactés</SelectItem>
                    <SelectItem value="client">Clients</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                          onCheckedChange={selectAllContacts}
                        />
                      </TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredContacts.slice(0, 20).map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedContacts.includes(contact.id)}
                            onCheckedChange={() => toggleContact(contact.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{contact.nom_entreprise}</p>
                            <p className="text-sm text-muted-foreground">{contact.nom_contact}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{contact.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {contact.statut === "lead" ? "Lead" :
                             contact.statut === "prospect" ? "Prospect" :
                             contact.statut === "contacte" ? "Contacté" :
                             contact.statut === "client" ? "Client" : contact.statut}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {contact.type_client}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {filteredContacts.length > 20 && (
                <p className="text-sm text-muted-foreground text-center mt-4">
                  Affichage de 20 contacts sur {filteredContacts.length}
                </p>
              )}
              
              {selectedContacts.length > 0 && (
                <div className="flex items-center justify-between mt-4 p-3 bg-primary/10 rounded-lg">
                  <span className="text-sm font-medium">
                    {selectedContacts.length} contact(s) sélectionné(s)
                  </span>
                  <Button 
                    size="sm"
                    onClick={() => setShowCampaignDialog(true)}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Créer une campagne
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Campaign Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Nouvelle campagne email
            </DialogTitle>
            <DialogDescription>
              Composez votre email et sélectionnez les destinataires
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
            {/* Left: Email composition */}
            <div className="space-y-4">
              <div>
                <Label>Nom de la campagne *</Label>
                <Input
                  value={campaignData.name}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Prospection Février 2026"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Template</Label>
                <Select value={campaignData.templateId} onValueChange={applyTemplate}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choisir un template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Objet de l'email *</Label>
                <Input
                  value={campaignData.subject}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Ex: Food Truck United - Vos événements méritent le meilleur"
                  className="mt-1"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between">
                  <Label>Contenu de l'email *</Label>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100"
                      onClick={() => setCampaignData(prev => ({ ...prev, content: prev.content + "{{nom_contact}}" }))}>
                      +nom_contact
                    </Badge>
                    <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100"
                      onClick={() => setCampaignData(prev => ({ ...prev, content: prev.content + "{{nom_entreprise}}" }))}>
                      +nom_entreprise
                    </Badge>
                  </div>
                </div>
                <Textarea
                  value={campaignData.content}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Rédigez votre email ici..."
                  className="mt-1 min-h-[250px] font-mono text-sm"
                />
              </div>
              
              <Button variant="outline" onClick={previewEmail} className="w-full">
                <Eye className="w-4 h-4 mr-2" />
                Prévisualiser
              </Button>
            </div>
            
            {/* Right: Recipients selection */}
            <div className="space-y-4">
              <div>
                <Label className="flex items-center justify-between">
                  <span>Destinataires ({selectedContacts.length} sélectionnés)</span>
                  <Button variant="link" size="sm" className="h-auto p-0" onClick={selectAllContacts}>
                    {selectedContacts.length === filteredContacts.length ? "Désélectionner tout" : "Sélectionner tout"}
                  </Button>
                </Label>
                
                <div className="flex gap-2 mt-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher..."
                      value={searchContact}
                      onChange={(e) => setSearchContact(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={contactFilter} onValueChange={setContactFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="lead">Leads</SelectItem>
                      <SelectItem value="prospect">Prospects</SelectItem>
                      <SelectItem value="contacte">Contactés</SelectItem>
                      <SelectItem value="client">Clients</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="border rounded-lg max-h-[350px] overflow-y-auto">
                {filteredContacts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-8 h-8 mx-auto mb-2" />
                    <p>Aucun contact trouvé</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredContacts.map((contact) => (
                      <div 
                        key={contact.id} 
                        className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer ${
                          selectedContacts.includes(contact.id) ? "bg-primary/5" : ""
                        }`}
                        onClick={() => toggleContact(contact.id)}
                      >
                        <Checkbox 
                          checked={selectedContacts.includes(contact.id)}
                          onCheckedChange={() => toggleContact(contact.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{contact.nom_entreprise}</p>
                          <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
                        </div>
                        <Badge variant="outline" className="text-xs flex-shrink-0">
                          {contact.statut}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Quick select buttons */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const leadIds = contacts.filter(c => c.statut === "lead" && c.email).map(c => c.id);
                    setSelectedContacts(leadIds);
                  }}
                >
                  Tous les leads ({contacts.filter(c => c.statut === "lead" && c.email).length})
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const prospectIds = contacts.filter(c => c.statut === "prospect" && c.email).map(c => c.id);
                    setSelectedContacts(prospectIds);
                  }}
                >
                  Tous les prospects ({contacts.filter(c => c.statut === "prospect" && c.email).length})
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={sendCampaign} 
              disabled={sending || selectedContacts.length === 0}
              className="bg-primary hover:bg-primary-hover"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Envoyer à {selectedContacts.length} contact(s)</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Modifier le template" : "Nouveau template"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom du template *</Label>
                <Input
                  value={templateData.name}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Relance Q1"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select value={templateData.category} onValueChange={(v) => setTemplateData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospection">Prospection</SelectItem>
                    <SelectItem value="relance">Relance</SelectItem>
                    <SelectItem value="invitation">Invitation</SelectItem>
                    <SelectItem value="fidelisation">Fidélisation</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Objet *</Label>
              <Input
                value={templateData.subject}
                onChange={(e) => setTemplateData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Objet de l'email"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Contenu *</Label>
              <Textarea
                value={templateData.content}
                onChange={(e) => setTemplateData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Contenu de l'email..."
                className="mt-1 min-h-[200px]"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Variables disponibles : {"{{nom_contact}}"}, {"{{nom_entreprise}}"}, {"{{email}}"}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveTemplate}>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Aperçu de l'email
            </DialogTitle>
          </DialogHeader>
          
          <div className="border rounded-lg p-4 bg-white">
            <div className="border-b pb-3 mb-4">
              <p className="text-sm text-muted-foreground">Objet :</p>
              <p className="font-medium">{previewContent.subject}</p>
            </div>
            <div className="whitespace-pre-wrap text-sm">
              {previewContent.content}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visual Email Template Editor */}
      {showVisualEditor && (
        <EmailTemplateEditor
          onSave={saveVisualTemplate}
          onCancel={() => {
            setShowVisualEditor(false);
            setEditingVisualTemplate(null);
          }}
          initialBlocks={editingVisualTemplate?.blocks || []}
          initialSubject={editingVisualTemplate?.subject || ""}
        />
      )}
    </div>
  );
}
